import { Check, Coffee, Gift, Plus, RotateCcw } from 'lucide-react';
import { useState, type CSSProperties } from 'react';

const colors = [
  { name: 'verde', background: '#214d3e', foreground: '#ffffff' },
  { name: 'coral', background: '#fa7053', foreground: '#291b17' },
  { name: 'azul', background: '#2547d0', foreground: '#ffffff' },
  { name: 'grafite', background: '#252525', foreground: '#ffffff' },
] as const;

type PassProps = {
  name: string;
  reward: string;
  current?: number;
  background?: string;
  foreground?: string;
  photo?: string;
  variant?: 'coffee' | 'flower' | 'barber';
  label?: string;
};

/** A live, deliberately local preview of the stamp-card fields supported by the product. */
export function LoyaltyPass({
  name,
  reward,
  current = 3,
  background = '#214d3e',
  foreground = '#ffffff',
  photo,
  variant = 'coffee',
  label = `Exemplo de cartão ${name}`,
}: PassProps) {
  return (
    <div
      className={`loyalty-pass loyalty-pass--${variant}`}
      role="group"
      aria-label={label}
      style={
        {
          '--pass-background': background,
          '--pass-foreground': foreground,
        } as CSSProperties
      }
    >
      <div className="pass-top">
        <span className="pass-brand">
          <span className="pass-brand-symbol" aria-hidden="true">
            {name.charAt(0).toUpperCase()}
          </span>
          <strong>{name}</strong>
        </span>
        <span className="pass-count">
          <small>VISITAS</small>
          <b>
            {current}
            <span> / 8</span>
          </b>
        </span>
      </div>
      {photo ? (
        <img
          className="pass-photo"
          src={photo}
          alt=""
          width="480"
          height="240"
          loading="lazy"
        />
      ) : (
        <div className="pass-typography" aria-hidden="true">
          {variant === 'flower'
            ? 'flores para\nficar perto.'
            : 'o seu\nmomento.'}
        </div>
      )}
      <div className="pass-stamps" role="progressbar" aria-label={`${current} de 8 carimbos`} aria-valuemin={0} aria-valuemax={8} aria-valuenow={current}>
        {Array.from({ length: 8 }, (_, index) => (
          <span
            className={index < current ? 'is-stamped' : ''}
            key={index}
            aria-hidden="true"
          >
            {index < current ? (
              <Check size={18} strokeWidth={2.2} />
            ) : index === 7 ? (
              <Gift size={17} />
            ) : (
              <Coffee size={16} strokeWidth={1.4} />
            )}
          </span>
        ))}
      </div>
      <div className="pass-reward">
        <small>SUA PRÓXIMA RECOMPENSA</small>
        <strong>{reward}</strong>
      </div>
      <div className="pass-bottom">
        <div>
          <small>CARTÃO DE FIDELIDADE</small>
          <span>Seu próximo motivo para voltar.</span>
        </div>
        <img src="/images/hero-barcode-qr.svg" width="52" height="52" alt="" />
      </div>
    </div>
  );
}

export function CardDemo() {
  const [name, setName] = useState('Café Aurora');
  const [reward, setReward] = useState('Um café por nossa conta');
  const [color, setColor] = useState<(typeof colors)[number]>(colors[0]);
  const [visits, setVisits] = useState(3);
  return (
    <div className="card-demo">
      <div className="card-demo__controls">
        <div className="demo-heading">
          <span className="section-kicker">Do seu jeito</span>
          <h3>
            Faça um teste.
            <br />O cartão é seu.
          </h3>
          <p>
            Mude a marca, escolha uma cor e veja uma visita virar recompensa.
          </p>
        </div>
        <label className="demo-field">
          Nome do negócio
          <input
            value={name}
            maxLength={32}
            onChange={(event) => setName(event.target.value)}
            autoComplete="off"
          />
        </label>
        <fieldset className="demo-palette">
          <legend>Cor do cartão</legend>
          <div>
            {colors.map((option) => (
              <button
                key={option.name}
                type="button"
                aria-label={`Cor ${option.name}`}
                aria-pressed={color.name === option.name}
                onClick={() => setColor(option)}
                style={{
                  background: option.background,
                  color: option.foreground,
                }}
              >
                {color.name === option.name && (
                  <Check size={18} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="demo-field">
          Recompensa
          <input
            value={reward}
            maxLength={44}
            onChange={(event) => setReward(event.target.value)}
            autoComplete="off"
          />
        </label>
        <p className="demo-note">
          Demonstração local. Nada é salvo ou enviado.
        </p>
      </div>
      <div className="card-demo__stage">
        <LoyaltyPass
          name={name.trim() || 'Seu negócio'}
          reward={reward.trim() || 'Sua recompensa'}
          background={color.background}
          foreground={color.foreground}
          current={visits}
          photo="/images/neighborhood-coffee.avif"
          label="Prévia do seu cartão"
        />
        <div className="demo-actions">
          <button
            className="demo-visit"
            type="button"
            disabled={visits === 8}
            onClick={() => setVisits((value) => Math.min(8, value + 1))}
          >
            <Plus size={17} aria-hidden="true" />
            Simular uma visita
          </button>
          <button
            className="demo-reset"
            type="button"
            aria-label="Reiniciar demonstração"
            onClick={() => setVisits(3)}
          >
            <RotateCcw size={17} aria-hidden="true" />
          </button>
        </div>
        <p className="demo-status" role="status" aria-live="polite">
          {visits === 8
            ? 'Recompensa liberada na demonstração.'
            : `${visits} visitas. Faltam ${8 - visits} para a recompensa.`}
        </p>
      </div>
    </div>
  );
}
